//Built-in libraries

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

//Custom components
import AttachmentContainer, { Attachment } from '../Attachments';

//Services
import {
  addApplicationFile,
  deleteApplicationFile,
  signApplicationPublicAsset,
} from 'api/Rover/ApplicationForm';
import { useFileUpload } from 'hooks/useFileUpload';
import { notifyUser } from 'store/Toast';
import { accountingFormatNumber, formatNumber } from 'utils';

//STP Libraries

//Assets and Miscellaneous
import {
  IGetApplicationServiceResDataFile,
  ISignApplicationPublicAssetPayload,
} from 'api/Rover/ApplicationForm/types';
import { SignedApplicationAssetResponse } from 'types/Shared';
import { RoverReactQueryKey } from 'constants/ReactQueryKeys';

//Component Begin

export interface IProjectFinanceProps {
  projectFinance: any;
  files: IGetApplicationServiceResDataFile[];
}
export default function ProjectFinancial({
  projectFinance,
  files,
}: IProjectFinanceProps) {
  const { applicationFormId, applicationId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const projectDetailsFiles = files.filter(
    (file) => file.field === 'financeDocs'
  );

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response: any) => {
        uploadApplicationFiles.mutate(response);
      },
      onError: (error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Failed to upload document`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadApplicationFiles = useMutation(
    (payload: {
      sign: SignedApplicationAssetResponse;
      files: File[];
      response: any;
    }) => {
      const { files, sign } = payload;
      return addApplicationFile({
        applicationId: String(applicationId),
        files: [
          {
            name: files[0].name,
            path: sign.path,
            key: sign.key,
            type: files[0].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          },
        ],
      });
    },
    {
      onSuccess: (_, payload: { files: File[] }) => {
        queryClient.invalidateQueries([
          RoverReactQueryKey.GET_APPLICATION_DETAILS,
        ]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${payload.files[0].name} is successfully uploaded.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Failed to upload document`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleUploadDocument = (files: File[]) => {
    const fileExist = projectDetailsFiles?.find(
      (doc) => doc?.name === files[0].name
    );
    if (fileExist) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `File already exists.`,
          toastType: 'failed',
        })
      );
    } else {
      uploadFile.mutate({
        signAssetPayload: [
          {
            fileName: files[0].name,
            fileType: files[0].type,
            fileSize: files[0].size,
            formId: String(applicationFormId),
          },
        ],
        files,
      });
    }
  };

  const deleteFile = useMutation(
    (payload: { key: string }) =>
      deleteApplicationFile({
        ...payload,
        applicationId: String(applicationId),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          RoverReactQueryKey.GET_APPLICATION_DETAILS,
        ]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Successfully deleted file`,
            toastType: 'success',
          })
        );
      },
    }
  );

  const handleFileDelete = (key: string) => {
    deleteFile.mutate({ key });
  };

  return (
    <>
      <section aria-labelledby='applicant-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='py-5'>
            <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
              <h2
                id='applicant-information-title'
                className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'
              >
                Project Financial
              </h2>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 mb-8'>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Sources</dt>
                {projectFinance.sources?.map((source: any) => {
                  return (
                    <dd className='mt-1 text-sm text-gray-900'>
                      {source.name}:{' '}
                      {accountingFormatNumber({
                        num: source.amount,
                        format: 'en-US',
                        currency: 'USD',
                      })}
                    </dd>
                  );
                })}
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Uses</dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  Acquisition:{' '}
                  {accountingFormatNumber({
                    num: projectFinance.uses.acquisition,
                    format: 'en-US',
                    currency: 'USD',
                  })}
                </dd>
                <dd className='mt-1 text-sm text-gray-900'>
                  Hard Cost:{' '}
                  {accountingFormatNumber({
                    num: projectFinance.uses.hardCost,
                    format: 'en-US',
                    currency: 'USD',
                  })}
                </dd>
                <dd className='mt-1 text-sm text-gray-900'>
                  Soft Cost:{' '}
                  {accountingFormatNumber({
                    num: projectFinance.uses.softCost,
                    format: 'en-US',
                    currency: 'USD',
                  })}
                </dd>
                <dd className='mt-1 text-sm text-gray-900'>
                  Finance Cost:{' '}
                  {accountingFormatNumber({
                    num: projectFinance.uses.financeCost,
                    format: 'en-US',
                    currency: 'USD',
                  })}
                </dd>
                <dd className='mt-1 text-sm text-gray-900'>
                  Developer Cost:{' '}
                  {accountingFormatNumber({
                    num: projectFinance.uses.developerFeeAmount,
                    format: 'en-US',
                    currency: 'USD',
                  })}
                </dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>
                  Will you be using HOME funds (www.home.com)?
                </dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>
                  {projectFinance?.willUseHomeFunds}
                </dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>
                  Will you be using CDBG funds(www.cdbg.com)?
                </dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>
                  {projectFinance?.willUseCDBGFunds}
                </dd>
              </div>
            </dl>
            <AttachmentContainer
              title='Additional Document'
              uploadConfig={{
                onUpload: handleUploadDocument,
                isUploading: uploadFile.isLoading,
              }}
              isEmpty={
                !projectDetailsFiles || projectDetailsFiles?.length === 0
              }
            >
              {projectDetailsFiles?.map((doc) => (
                <Attachment
                  title={doc?.name || ''}
                  doc={doc}
                  key={doc?.url}
                  deleteFileHandler={handleFileDelete}
                  onClick={() => {
                    window.open(doc?.url || '');
                  }}
                />
              ))}
            </AttachmentContainer>
          </div>
        </div>
      </section>
    </>
  );
}
